import React, { useCallback } from "react";
import styled from "styled-components";
import {
  FontSize,
  FontWeight,
  Radius,
  Transition,
  dominant,
  ellipsis,
  form,
  transitions,
} from "@glossy";

interface IProps {
  // State of the switch. True or false.
  checked: boolean;
  // Callback fired when the state is changed.
  onChange: (e: any, checked: boolean) => void;
  // Is the switch disabled
  disabled?: boolean;
  // Label displayed after the switch
  label?: string | React.ReactNode;
  // Additionnal class names
  className?: string;
}

const Root = styled.div<{ checked: boolean; disabled: boolean }>`
  position: relative;
  padding-left: 38px;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  opacity: ${(props) => props.disabled && 0.5};
  display: inline-block;
  line-height: 20px;
  min-height: 20px;

  &:before,
  &:after {
    content: "";
    position: absolute;
  }

  /* Border */
  &:before {
    left: 0;
    top: 1px;
    height: 18px;
    width: 32px;
    border-radius: 9px;
    box-sizing: border-box;
    border-style: solid;
    border-width: ${(props) => (props.checked ? 2 : 1)}px;
    border-color: ${(props) => (props.checked ? dominant : form)};
    ${transitions("backgroundColor", Transition.Length)};
  }

  /* Toggle */
  &:after {
    left: ${(props) => (props.checked ? 18 : 3)}px;
    top: 5px;
    width: 10px;
    height: 10px;
    border-radius: ${Radius.Medium};
    background-color: ${(props) => (props.checked ? dominant : form)};
    ${transitions(["backgroundColor", "left"], Transition.Length)};
  }

  &:hover:after {
    background-color: ${(props) => !props.disabled && dominant};
  }

  &:hover:before {
    border-color: ${(props) => !props.checked && form};
  }
`;

const Label = styled.div<{ checked: boolean; disabled: boolean }>`
  vertical-align: middle;
  color: ${(props) => (props.checked ? dominant : form)};
  font-size: ${FontSize.Medium};
  font-weight: ${(props) =>
    props.checked ? FontWeight.Bold : FontWeight.Normal};
  ${ellipsis()};
  white-space: break-spaces;
`;

const useToggle = (disabled, checked, onChange) =>
  useCallback(
    (e) => {
      !disabled && onChange(e, !checked);
    },
    [checked, onChange, disabled]
  );

const UiKitFormToggle = ({
  label,
  checked,
  onChange,
  disabled,
  className,
}: IProps) => {
  const onToggle = useToggle(disabled, checked, onChange);

  return (
    <Root
      disabled={!!disabled}
      checked={checked}
      className={className}
      onClick={onToggle}
    >
      <Label disabled={!!disabled} checked={checked}>
        {label}
      </Label>
    </Root>
  );
};

export default UiKitFormToggle;
